/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1682380800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Apr 25 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "maxio",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Maxio",
          "depth": 0,
          "id": "connect-to-maxio"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing customers",
          "depth": 1,
          "id": "syncing-customers"
        }, {
          "value": "Syncing subscriptions",
          "depth": 1,
          "id": "syncing-subscriptions"
        }, {
          "value": "Syncing usage data",
          "depth": 1,
          "id": "syncing-usage-data"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customers"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Customers"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.chargify.com/docs/api-docs/18237bcfe5cbb-create-customer",
                children: "Customers docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subscriptions"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Subscriptions"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.chargify.com/docs/api-docs/d571659cf0f24-create-subscription",
                children: "Subscriptions docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Usage"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Usage"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.chargify.com/docs/api-docs/9954fb40d117d-create-usage",
                children: "Usage docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Events-Based Billing"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.chargify.com/docs/api-docs/b8ffb590f236a-event-ingestion",
                children: "Event docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-maxio",
        children: "Connect to Maxio"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Maxio"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Maxio"
        }), " by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Subdomain"
          }), ": Part of your URL, for example, ", _jsx(_components.code, {
            children: "example-subdomain"
          }), " in ", _jsx(_components.code, {
            children: "https://example-subdomain.chargify.com"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "API key"
          }), ": A valid Maxio API key, follow ", _jsx(_components.a, {
            href: "https://maxio-chargify.zendesk.com/hc/en-us/articles/5405281550477#api",
            children: "Maxio's instructions"
          }), " to view or create a new API key"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The same one you used to log into your account"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Maxio destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Maxio destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-customers",
        children: "Syncing customers"
      }), "\n", _jsx(_components.p, {
        children: "When syncing customers, you can choose from upsert, update, or insert modes."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to customers in Maxio, you need to select a model column and corresponding Maxio field. You can match on any of the following Maxio fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "id"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "reference"
          })
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, Maxio automatically generates an identifier for every new record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in Maxio. Maxio requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "first_name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "last_name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "email"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Ensure the data types of your model columns match the data types of the fields you want to sync to."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the record in Maxio with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Unset all the mapped fields, but keep the record in Maxio"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced record from your Maxio customer"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-subscriptions",
        children: "Syncing subscriptions"
      }), "\n", _jsx(_components.p, {
        children: "When syncing subscriptions, you can choose from upsert, update, or insert modes."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to records in Maxio, you need to select the model column that contains values that match the ", _jsx(_components.strong, {
          children: "reference"
        }), " field."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, Maxio automatically generates an identifier for every new record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in Maxio. Maxio requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "customer_attributes"
          }), ", ", _jsx(_components.strong, {
            children: "customer_id"
          }), " or ", _jsx(_components.strong, {
            children: "customer_reference"
          })]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "payment_profile_id"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "product_handle"
          }), " or ", _jsx(_components.strong, {
            children: "product_id"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Ensure the data types of your model columns match the data types of the fields you want to sync to."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-usage-data",
        children: "Syncing usage data"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you report usage events to Maxio. To do so, you must select the Maxio subscription and component you would like to sync to."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Maxio automatically generates an identifier for every new record synced, so there is no need to match an existing record."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync columns from your model to Maxio default usage fields. Check out Maxio's ", _jsx(_components.a, {
          href: "https://developers.chargify.com/docs/api-docs/9954fb40d117d-create-usage",
          children: "usage docs"
        }), " to learn more."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsx(_components.p, {
        children: "Maxio requires the following event parameters:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "api-handle"
          }), ": You can either provide a static value or select to ", _jsx(_components.strong, {
            children: "use a column"
          }), " from your model."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "subdomain"
          }), ": Hightouch uses the subdomain you entered when ", _jsx(_components.a, {
            href: "#connect-to-maxio",
            children: "connecting to Maxio"
          })]
        }), "\n"]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally select a column that contains timestamps of when events occurred. If this field is empty, Hightouch uses the time the event arrives at the server. Hightouch syncs the timestamp as the ", _jsx(_components.code, {
          children: "event_time"
        }), " parameter the Maxio requires."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-3",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Maxio automatically generates an identifier for every new event synced, so there is no need to match an existing record."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-3",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you sync event properties via default and custom mappings."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
